'use client'

import { motion } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'

/**
 * Loading Text Animation Library
 *
 * A unified collection of loading text animations that can be applied
 * consistently across the application. All animations are configurable
 * and support theming.
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

export type LoadingAnimationType =
  | 'wave' // Wave passes over text (opacity + movement)
  | 'bounce' // Bounce up and down with opacity fade
  | 'icon-spinner' // Cycles through icons

export interface LoadingTextConfig {
  /** Animation style to use */
  type: LoadingAnimationType
  /** Text to display */
  text: string
  /** Text color (CSS color value) */
  color?: string
  /** Font size (CSS font-size value) */
  fontSize?: string
  /** Base animation speed multiplier (1 = normal) */
  speed?: number
  /** Enable dynamic speed variations */
  dynamicSpeed?: boolean
}

// ============================================================================
// Wave Animation (Shadow wave with traveling movement)
// ============================================================================

interface WaveAnimationProps {
  text: string
  color: string
  fontSize: string
  baseSpeed?: number
  dynamicSpeed?: boolean
  style?: React.CSSProperties
  className?: string
}

function WaveAnimation({
  text,
  color,
  fontSize,
  baseSpeed = 1,
  dynamicSpeed = true,
  style,
  className,
}: WaveAnimationProps) {
  const [speedMultiplier, setSpeedMultiplier] = useState(1)

  // Occasionally change animation speed at random intervals
  useEffect(() => {
    if (!dynamicSpeed) return

    const scheduleNextSpeedChange = () => {
      const delay = 4000 + Math.random() * 8000

      const timeout = setTimeout(() => {
        const rand = Math.random()
        let newSpeed: number

        if (rand < 0.15) {
          newSpeed = 0.6 + Math.random() * 0.2
        } else if (rand < 0.3) {
          newSpeed = 0.85 + Math.random() * 0.1
        } else if (rand < 0.7) {
          newSpeed = 0.95 + Math.random() * 0.1
        } else if (rand < 0.85) {
          newSpeed = 1.05 + Math.random() * 0.15
        } else {
          newSpeed = 1.3 + Math.random() * 0.3
        }

        setSpeedMultiplier(newSpeed)
        scheduleNextSpeedChange()
      }, delay)

      return timeout
    }

    const timeout = scheduleNextSpeedChange()
    return () => clearTimeout(timeout)
  }, [dynamicSpeed])

  // (debug logging removed for cleaner console)

  const charAnimations = useMemo(() => {
    const waveDuration = (4 + Math.random() * 2) * baseSpeed

    // Create grouped movement pattern
    const movementGroups: boolean[] = []
    let i = 0
    while (i < text.length) {
      const shouldGroup = Math.random() > 0.6

      if (shouldGroup) {
        const groupSize = 2 + Math.floor(Math.random() * 3)
        for (let j = 0; j < groupSize && i < text.length; j++, i++) {
          movementGroups[i] = true
        }
      } else {
        movementGroups[i] = false
        i++
      }
    }

    return {
      waveDuration,
      chars: text.split('').map((_, index) => {
        const charPositionRatio = index / text.length
        const wavePassTime = charPositionRatio * (waveDuration / 2)
        const randomOffset =
          Math.random() < 0.4 ? -(Math.random() * 0.2) : Math.random() * 0.3
        const movementDelay = wavePassTime * (1 + randomOffset)

        const isFast = Math.random() < 0.1
        const movementDuration = isFast
          ? 2 + Math.random() * 0.5
          : 3.5 + Math.random() * 1

        return {
          movementDuration,
          movementDelay,
          yMovement: -0.8 - Math.random() * 0.4,
          shouldMove: movementGroups[index],
        }
      }),
    }
  }, [text, baseSpeed])

  // Baseline tweak so animated text visually matches normal text baseline
  const BASELINE_TWEAK_PX = -1

  return (
    <span className={className} style={style}>
      {text.split('').map((char, index) => {
        const anim = charAnimations.chars[index]
        const totalChars = text.length

        const opacityKeyframes: number[] = []
        const yKeyframes: number[] = []

        for (let i = 0; i <= 10; i++) {
          const progress = i / 10
          const wavePos =
            progress <= 0.5
              ? progress * 2 * totalChars
              : (1 - (progress - 0.5) * 2) * totalChars

          const distance = Math.abs(index - wavePos)
          const opacity = Math.min(1, 0.3 + distance / 3)
          opacityKeyframes.push(opacity)

          let yPos = BASELINE_TWEAK_PX
          if (anim.shouldMove && distance < 3) {
            const intensity = Math.max(0, 1 - distance / 3)
            yPos = BASELINE_TWEAK_PX + anim.yMovement * intensity
          }
          yKeyframes.push(yPos)
        }

        return (
          <motion.span
            key={index}
            animate={{
              opacity: opacityKeyframes,
              y: yKeyframes,
            }}
            transition={{
              duration: charAnimations.waveDuration * speedMultiplier,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              display: 'inline-block',
              verticalAlign: 'baseline',
              whiteSpace: char === ' ' ? 'pre' : 'normal',
              color,
              fontSize,
            }}
          >
            {char}
          </motion.span>
        )
      })}
    </span>
  )
}

// ============================================================================
// Bounce Animation (Bounce with opacity fade)
// ============================================================================

interface BounceAnimationProps {
  text: string
  color: string
  fontSize: string
  baseSpeed?: number
}

function BounceAnimation({
  text,
  color,
  fontSize,
  baseSpeed = 1,
}: BounceAnimationProps) {
  return (
    <motion.div
      animate={{
        y: [0, -3, 0],
        opacity: [0.6, 1, 0.6],
      }}
      transition={{
        duration: 1.2 * baseSpeed,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      style={{ color, fontSize, display: 'inline-block' }}
    >
      {text}
    </motion.div>
  )
}

// ============================================================================
// Icon Spinner Animation (Cycles through icons)
// ============================================================================

interface IconSpinnerAnimationProps {
  text: string
  color: string
  fontSize: string
  baseSpeed?: number
}

function IconSpinnerAnimation({
  text,
  color,
  fontSize,
  baseSpeed = 1,
}: IconSpinnerAnimationProps) {
  // Weather icons to cycle through
  const icons = ['○', '◉', '▪', '❅', '≡', '※']
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % icons.length)
    }, 400 * baseSpeed)

    return () => clearInterval(interval)
  }, [baseSpeed])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color,
        fontSize,
      }}
    >
      <motion.span
        key={currentIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        style={{
          display: 'inline-block',
          fontWeight: 'bold',
          minWidth: '1em',
          textAlign: 'center',
        }}
      >
        {icons[currentIndex]}
      </motion.span>
      <motion.span
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        {text}
      </motion.span>
    </div>
  )
}

// ============================================================================
// Main Loading Text Component
// ============================================================================

export interface LoadingTextProps extends LoadingTextConfig {
  /** Additional CSS styles */
  style?: React.CSSProperties
  /** CSS class name */
  className?: string
}

export function LoadingText({
  type,
  text,
  color = 'inherit',
  fontSize = '14px',
  speed = 1,
  dynamicSpeed = true,
  style,
  className,
}: LoadingTextProps) {
  const commonProps = {
    text,
    color,
    fontSize,
    baseSpeed: speed,
    style,
    className,
  }

  // We don't use a wrapper div anymore to avoid box model mismatches.
  // The styles are passed directly to the animation container.
  switch (type) {
    case 'wave':
      return <WaveAnimation {...commonProps} dynamicSpeed={dynamicSpeed} />
    case 'bounce':
      // Bounce/Widget might still need a wrapper or update. 
      // For now, we focus on Wave (Thinking) as it's the main issue.
      return (
        <div className={className} style={{ display: 'inline-block', ...style }}>
          <BounceAnimation {...commonProps} />
        </div>
      )
    case 'icon-spinner':
      return (
        <div className={className} style={{ display: 'inline-block', ...style }}>
          <IconSpinnerAnimation {...commonProps} />
        </div>
      )
    default:
      return <div style={{ color, fontSize, ...style }} className={className}>{text}</div>
  }
}

// ============================================================================
// Preset Configurations
// ============================================================================

export const loadingPresets = {
  /** Sophisticated wave animation with movement (default for thinking states) */
  thinking: (text: string, color?: string): LoadingTextConfig => ({
    type: 'wave',
    text,
    color,
    dynamicSpeed: true,
  }),

  /** Bounce animation for loading states */
  loading: (text: string, color?: string): LoadingTextConfig => ({
    type: 'bounce',
    text,
    color,
  }),

  /** Icon spinner animation for widgets (weather, clock, etc.) */
  widget: (text: string, color?: string): LoadingTextConfig => ({
    type: 'icon-spinner',
    text,
    color,
  }),
}

/**
 * Example usage:
 *
 * // Using presets
 * <LoadingText {...loadingPresets.thinking('Assistant is thinking...')} />
 *
 * // Custom configuration
 * <LoadingText
 *   type="wave"
 *   text="Loading..."
 *   color="#123"
 *   fontSize="16px"
 *   speed={1.5}
 *   dynamicSpeed={false}
 * />
 */
